import React, { useState, useEffect } from 'react';
import Nav from './nav';

const PortfolioChart = ({ invested, earnings }) => {
  const total = invested + earnings;
  const investedPercentage = (invested / total) * 100;
  const earningsPercentage = (earnings / total) * 100;

  return (
    <div className="relative w-32 h-32 mx-auto mb-4">
      <svg viewBox="0 0 36 36" className="w-full h-full">
        <circle
          cx="18"
          cy="18"
          r="15.9155"
          className="fill-none stroke-gray-600"
          strokeWidth="2"
        />
        <path
          className="fill-none stroke-green-500"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${earningsPercentage}, 100`}
          d="M18 2.0845
            a 15.9155 15.9155 0 0 1 0 31.831
            a 15.9155 15.9155 0 0 1 0 -31.831"
        />
        <path
          className="fill-none stroke-blue-500"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray={`${investedPercentage} ${100 - investedPercentage}`}
          strokeDashoffset={`-${earningsPercentage}`}
          d="M18 2.0845
            a 15.9155 15.9155 0 0 1 0 31.831
            a 15.9155 15.9155 0 0 1 0 -31.831"
        />
        <text x="18" y="18" textAnchor="middle" className="fill-white text-xs font-bold" dy=".3em">
          ${(total / 1000).toFixed(1)}k
        </text>
      </svg>
      
      <div className="absolute -bottom-8 left-0 right-0 flex justify-center space-x-4 text-xs">
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          <span className="text-gray-400">Invested</span>
        </div>
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
          <span className="text-gray-400">Earnings</span>
        </div>
      </div>
    </div>
  );
};

const PlanModal = ({ plan, isOpen, onClose, onInvest }) => {
  if (!isOpen || !plan) return null;

  const getCoinImage = (symbol) => {
    const coins = [
      { symbol: 'BTC', name: 'Bitcoin', image: '/images/coins/btc.jpg' },
      { symbol: 'ETH', name: 'Ethereum', image: '/images/coins/eth.jpg' },
      { symbol: 'BDX', name: 'Beldex', image: '/images/coins/bdx.png' },
      { symbol: 'TRX', name: 'Tron', image: '/images/coins/trx.png' },
      { symbol: 'XRP', name: 'Ripple', image: '/images/coins/xrp.png' }
    ];
    const coin = coins.find(c => c.symbol === symbol);
    return coin ? coin.image : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iMjAiIGZpbGw9IiMzNzM3NDAiLz4KPC9zdmc+';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-end justify-center z-50 px-4 pb-4 sm:items-center sm:p-4">
      <div className="bg-gray-800 rounded-2xl w-full max-w-md max-h-[90vh] overflow-hidden animate-slide-up">
        {/* Header */}
        <div className="relative p-6 border-b border-gray-700">
          <div className="flex items-center space-x-4">
            <div className={`w-16 h-16 bg-gradient-to-r ${plan.color} rounded-xl flex items-center justify-center text-white text-2xl font-bold flex-shrink-0`}>
              {plan.coin ? (
                <img 
                  src={getCoinImage(plan.coin)} 
                  alt={plan.coin}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                plan.icon
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="text-xl font-bold text-white truncate">
                {plan.coin ? `${plan.coin} Staking` : plan.name}
              </h2>
              <div className="flex items-center space-x-2 mt-1 flex-wrap">
                <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-1 rounded-full mb-1">
                  {plan.duration}
                </span>
                <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full mb-1">
                  {plan.frequency}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto max-h-[60vh] p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-700/50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-green-400">{plan.dailyReturn}%</div>
              <div className="text-xs text-gray-400 mt-1">Daily Return</div>
            </div>
            <div className="bg-gray-700/50 rounded-lg p-4 text-center">
              <div className="text-2xl font-bold text-blue-400">{plan.totalReturn}%</div>
              <div className="text-xs text-gray-400 mt-1">Total Return</div>
            </div>
          </div>

          <div className="bg-gray-700/30 rounded-lg p-4">
            <h3 className="font-semibold text-gray-300 mb-3">Investment Range</h3>
            <div className="flex justify-between items-center">
              <span className="text-gray-400">Minimum</span>
              <span className="font-bold text-white">${plan.minAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center mt-2">
              <span className="text-gray-400">Maximum</span>
              <span className="font-bold text-white">${plan.maxAmount.toLocaleString()}</span>
            </div>
          </div>

          {plan.description && (
            <div className="bg-gray-700/30 rounded-lg p-4">
              <h3 className="font-semibold text-gray-300 mb-2">Plan Description</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{plan.description}</p>
            </div>
          )}

          {/* Features */}
          <div className="bg-gray-700/30 rounded-lg p-4">
            <h3 className="font-semibold text-gray-300 mb-3">Features</h3>
            <ul className="space-y-2">
              <li className="flex items-center space-x-3">
                <i className="fas fa-check text-green-400 text-sm flex-shrink-0"></i>
                <span className="text-sm text-gray-400">Daily compounding returns</span>
              </li>
              <li className="flex items-center space-x-3">
                <i className="fas fa-check text-green-400 text-sm flex-shrink-0"></i>
                <span className="text-sm text-gray-400">Principal included in returns</span>
              </li>
              <li className="flex items-center space-x-3">
                <i className="fas fa-check text-green-400 text-sm flex-shrink-0"></i>
                <span className="text-sm text-gray-400">24/7 monitoring</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t border-gray-700 flex space-x-3 bg-gray-800 sticky bottom-0">
          <button
            onClick={onClose}
            className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onInvest(plan)}
            className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 px-4 rounded-xl transition-all transform hover:scale-105"
          >
            Invest Now
          </button>
        </div>
      </div>
    </div>
  );
};

const EarnPage = () => {
  const [portfolioData, setPortfolioData] = useState({
    invested: 1165.30,
    earnings: 250.75
  });

  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const coins = [
    { symbol: 'BTC', name: 'Bitcoin', image: 'images/coins/btc.jpg' },
    { symbol: 'ETH', name: 'Ethereum', image: 'images/coins/eth.jpg' },
    { symbol: 'BDX', name: 'Beldex', image: 'images/coins/bdx.png' },
    { symbol: 'TRX', name: 'Tron', image: 'images/coins/trx.png' },
    { symbol: 'XRP', name: 'Ripple', image: 'images/coins/xrp.png' }
  ];

  const stakingPlans = [
    {
      id: 1,
      name: "Tesla",
      duration: "30 Days",
      frequency: "Daily",
      minAmount: 500,
      maxAmount: 9999,
      dailyReturn: 30,
      totalReturn: 900,
      icon: "🚀",
      color: "from-blue-500 to-cyan-500",
      category: "stocks",
      description: "Invest in Tesla's innovative technology and sustainable energy solutions."
    },
    {
      id: 2,
      coin: "BTC",
      duration: "30 Days",
      frequency: "Daily",
      minAmount: 1000,
      maxAmount: 19999,
      dailyReturn: 45,
      totalReturn: 1350,
      color: "from-orange-500 to-yellow-500",
      category: "crypto",
      description: "Bitcoin staking with high returns and secure blockchain technology."
    },
    {
      id: 3,
      name: "Microsoft",
      duration: "30 Days",
      frequency: "Daily",
      minAmount: 5000,
      maxAmount: 39999,
      dailyReturn: 50,
      totalReturn: 1500,
      icon: "💼",
      color: "from-green-500 to-emerald-500",
      category: "stocks",
      description: "Microsoft enterprise solutions and cloud computing investments."
    },
    {
      id: 4,
      coin: "BDX",
      duration: "1 Week",
      frequency: "Daily",
      minAmount: 100,
      maxAmount: 5000,
      dailyReturn: 15,
      totalReturn: 105,
      color: "from-purple-500 to-pink-500",
      category: "crypto",
      description: "Beldex privacy-focused cryptocurrency staking plan."
    },
    {
      id: 5,
      name: "Cocacola",
      duration: "1 Month",
      frequency: "Daily",
      minAmount: 500,
      maxAmount: 10000,
      dailyReturn: 30,
      totalReturn: 900,
      icon: "🥤",
      color: "from-red-500 to-orange-500",
      category: "stocks",
      description: "Invest in the world's leading beverage company with stable returns."
    }
  ];

  const statsCards = [
    {
      title: "Total Invested",
      value: `$${portfolioData.invested.toLocaleString()}`,
      icon: "📈",
      color: "from-blue-600 to-cyan-600",
      trend: "+12.5%",
      trendColor: "text-green-400"
    },
    {
      title: "Total Earnings",
      value: `$${portfolioData.earnings.toLocaleString()}`,
      icon: "💰",
      color: "from-green-600 to-emerald-600",
      trend: "+8.2%",
      trendColor: "text-green-400"
    }
  ];

  const filters = [
    { id: 'all', label: 'All Plans' },
    { id: 'stocks', label: 'Stocks' },
    { id: 'crypto', label: 'Crypto' },
    { id: 'high', label: 'High Return' }
  ];

  const filteredPlans = stakingPlans.filter(plan => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'stocks') return plan.category === 'stocks';
    if (activeFilter === 'crypto') return plan.category === 'crypto';
    if (activeFilter === 'high') return plan.dailyReturn >= 40;
    return true;
  });

  const getCoinImage = (symbol) => {
    const coin = coins.find(c => c.symbol === symbol);
    return coin ? coin.image : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iMjAiIGZpbGw9IiMzNzM3NDAiLz4KPC9zdmc+';
  };

  const handlePlanClick = (plan) => {
    setSelectedPlan(plan);
    setIsModalOpen(true);
  };

  const handleInvest = (plan) => {
    setIsModalOpen(false);
    window.location.href = `/user/deposit`;
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPlan(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white pb-20">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Staking Plans
            </h1>
            <a 
              href="earn-history.php" 
              className="flex items-center space-x-2 text-blue-400 hover:text-blue-300 font-semibold transition-colors group"
            >
              <span className="hidden sm:inline">My Investments</span>
              <i className="fas fa-history group-hover:rotate-180 transition-transform"></i>
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Portfolio Overview */}
        <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl border border-gray-700 p-6 mb-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex-1 text-center md:text-left mb-4 md:mb-0">
              <h2 className="text-xl font-bold mb-2">Portfolio Overview</h2>
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                {statsCards.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.title}</div>
                    <div className={`text-xs ${stat.trendColor}`}>{stat.trend}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex-shrink-0">
              <PortfolioChart 
                invested={portfolioData.invested} 
                earnings={portfolioData.earnings} 
              />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2 scrollbar-hide">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                activeFilter === filter.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                  : 'bg-gray-700/50 text-gray-300 hover:bg-gray-600/50'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Staking Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredPlans.map((plan) => (
            <div
              key={plan.id}
              className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-6 hover:border-gray-600 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/10 cursor-pointer group"
              onClick={() => handlePlanClick(plan)}
            >
              {/* Plan Header */}
              <div className="flex items-center space-x-4 mb-4">
                <div className={`w-14 h-14 bg-gradient-to-r ${plan.color} rounded-xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0 group-hover:scale-110 transition-transform`}>
                  {plan.coin ? (
                    <img 
                      src={getCoinImage(plan.coin)} 
                      alt={plan.coin}
                      className="w-8 h-8 rounded-full"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iMjAiIGZpbGw9IiMzNzM3NDAiLz4KPC9zdmc+';
                      }}
                    />
                  ) : (
                    plan.icon
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-white truncate">
                    {plan.coin ? `${plan.coin} Staking` : plan.name}
                  </h3>
                  <div className="flex items-center space-x-2 mt-1 flex-wrap">
                    <span className="bg-blue-500/20 text-blue-400 text-xs px-2 py-1 rounded-full mb-1">
                      {plan.duration}
                    </span>
                    <span className="bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full mb-1">
                      {plan.frequency}
                    </span>
                  </div>
                </div>
              </div>

              {/* Returns */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-green-400">{plan.dailyReturn}%</div>
                  <div className="text-xs text-gray-400">Daily</div>
                </div>
                <div className="bg-gray-700/30 rounded-lg p-3 text-center">
                  <div className="text-lg font-bold text-blue-400">{plan.totalReturn}%</div>
                  <div className="text-xs text-gray-400">Total</div>
                </div>
              </div>

              {/* Investment Range */}
              <div className="bg-gray-700/20 rounded-lg p-3 mb-4">
                <div className="text-xs text-gray-400 mb-1">Investment Range</div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">${plan.minAmount.toLocaleString()}</span>
                  <span className="text-gray-400 text-xs">to</span>
                  <span className="text-sm font-medium">${plan.maxAmount.toLocaleString()}</span>
                </div>
              </div>

              {/* Action Button */}
              <button className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold py-3 px-4 rounded-xl transition-all transform group-hover:scale-105">
                Invest Now
              </button>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {filteredPlans.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-search text-gray-400 text-2xl"></i>
            </div>
            <h3 className="text-xl font-bold text-gray-300 mb-2">No plans found</h3>
            <p className="text-gray-400">Try adjusting your filters to see more options.</p>
          </div>
        )}
      </div>

      {/* Plan Modal */}
      <PlanModal
        plan={selectedPlan}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onInvest={handleInvest}
      />

      {/* Navigation */}
      <Nav />

      {/* Include Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default EarnPage;